import * as React from "react";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Stack,
	Typography,
} from "@mui/material";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { RocketLaunchIcon } from "@phosphor-icons/react/dist/ssr/RocketLaunch";

import { useCampaignPreparation } from "@/hooks/api/use-campaign-preparation";
import type { CampaignRequestDetail, CampaignRequestMarket, ProvisionResult } from "@/hooks/api/use-campaign-requests";
import { useProvisionCampaignRequest, useTransitionCampaignRequest } from "@/hooks/api/use-campaign-requests";

import { MarketScheduleCard } from "../../components";
import { canTransition, marketLabel, REQUEST_STATE_LABELS } from "../../constants";
import { labelOf, requestTitle } from "../../humanise";
import { agentForMarketCard } from "../../view/agent-naming";
import { MarketBindings } from "./market-bindings";
import type { Readiness } from "./readiness";

/**
 * Bind market groups and numbers, then create disabled campaigns from approved settings.
 * Retries reuse each market's campaign and reapply its approved configuration.
 */
export function ProvisionTab({
	request,
	readiness,
}: {
	request: CampaignRequestDetail;
	readiness: Readiness;
}): React.JSX.Element {
	const { markets, unprovisioned, alreadyProvisioned, numbersReserved, groupsBound, isProvisionable } = readiness;

	const provisionMutation = useProvisionCampaignRequest();
	const { data: preparation, isError: preparationFailed } = useCampaignPreparation(request.id);
	const transitionMutation = useTransitionCampaignRequest();

	const [confirmOpen, setConfirmOpen] = React.useState(false);
	const [result, setResult] = React.useState<ProvisionResult | null>(null);

	const agentById = new Map(request.agents.map((agent) => [agent.id, agent]));
	const cardAgent = (agentId: string | null): CampaignRequestDetail["agents"][number] | null => {
		const agent = agentId ? agentById.get(agentId) : undefined;
		return agent ? agentForMarketCard(agent) : null;
	};

	// The mutation reports its own failures as a toast, verbatim off the API — a refused transition
	// or a missing market arrives as a 422 with a sentence worth reading. Swallowed here so the
	// rejection does not escape the click handler; the dialog stays open so the button can be
	// pressed again once whatever it complained about is fixed.
	const handleProvision = async (): Promise<void> => {
		try {
			const provisioned = await provisionMutation.mutateAsync(request.id);
			setResult(provisioned);
			setConfirmOpen(false);
		} catch {
			/* reported by the mutation */
		}
	};

	return (
		<Stack spacing={3}>
			<MarketBindings request={request} markets={markets} />
			{preparationFailed ? (
				<Alert severity="error">
					Could not read this request&rsquo;s preparation run, so provisioning stays disabled. Reload to try again.
				</Alert>
			) : preparation?.run?.status === "approved" && preparation?.proving.status === "passed" ? null : (
				<Alert severity="info">
					Complete admin approval and pass all ten Proving Ground tests per agent before creating campaigns.
				</Alert>
			)}

			<Divider />

			<Box>
				<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
					What This Will Create
				</Typography>

				{markets.length === 0 ? (
					<Alert severity="warning">This request has no markets, so there is nothing to build.</Alert>
				) : (
					<Stack spacing={1.5}>
						<Typography variant="body2" color="text.secondary">
							<strong>One campaign programme</strong>
							{request.campaignProgrammeId
								? " — already created, and reused rather than built again"
								: `, reporting in ${markets[0].timezone}`}
						</Typography>

						<Typography variant="body2" color="text.secondary">
							<strong>
								{unprovisioned.length} campaign{unprovisioned.length === 1 ? "" : "s"}
							</strong>
							{alreadyProvisioned.length > 0
								? ` — ${alreadyProvisioned.length} market${
										alreadyProvisioned.length === 1 ? " is" : "s are"
									} already built and reused`
								: ", one per market, using the approved configuration"}
						</Typography>

						{markets.map((market: CampaignRequestMarket) => (
							<MarketScheduleCard key={market.id} market={market} agent={cardAgent(market.agentId)} />
						))}
					</Stack>
				)}
			</Box>

			{/* What it created, after the click — in markets and counts. The uuids the API answers
			    with were printed here verbatim, which is a row of forty-character identifiers where
			    the reader wanted "Singapore, Malaysia". */}
			{result ? (
				<Alert severity="success" icon={<CheckCircleIcon />}>
					<AlertTitle>
						{result.created.length > 0
							? `Provisioned ${result.created.length} market${result.created.length === 1 ? "" : "s"}`
							: "Campaign configuration is complete"}
					</AlertTitle>
					<Typography variant="body2">
						{result.created.length > 0
							? `${result.created.map((code) => marketLabel(code)).join(", ")} · ${result.campaignIds.length} campaign${
									result.campaignIds.length === 1 ? "" : "s"
								} on one programme.`
							: "Existing campaigns were reused and their approved configuration was reapplied."}
					</Typography>
				</Alert>
			) : null}

			{isProvisionable ? null : (
				<Alert severity="warning">
					This request is <strong>{labelOf(REQUEST_STATE_LABELS, request.state)}</strong>. Provisioning is only
					available once it is <strong>in preparation</strong> or <strong>ready</strong>.
				</Alert>
			)}

			<Stack direction={{ sm: "row", xs: "column" }} spacing={1}>
				<Button
					variant="contained"
					startIcon={<RocketLaunchIcon />}
					disabled={
						!isProvisionable ||
						preparation?.run?.status !== "approved" ||
						preparation?.proving.status !== "passed" ||
						markets.length === 0 ||
						provisionMutation.isPending
					}
					onClick={() => setConfirmOpen(true)}
				>
					{unprovisioned.length > 0
						? `Create ${unprovisioned.length} disabled campaign${unprovisioned.length === 1 ? "" : "s"}`
						: "Reapply approved configuration"}
				</Button>

				<Button
					variant="outlined"
					color="secondary"
					disabled={
						!!request.provisionedAt ||
						!canTransition(request.state, "changes_requested") ||
						transitionMutation.isPending
					}
					onClick={() => transitionMutation.mutate({ id: request.id, state: "changes_requested" })}
				>
					Request Changes From Client
				</Button>
			</Stack>

			{request.provisionedAt ? (
				<Typography variant="body2" color="text.secondary">
					Campaigns already exist. Use Return to review in Processing &amp; Approval for prompt corrections. Changes to
					the brief, leads or commercial terms need a new request.
				</Typography>
			) : null}

			<Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Provision {requestTitle(request.title)}?</DialogTitle>
				<DialogContent>
					<Stack spacing={1} sx={{ pt: 1 }}>
						<Typography variant="body2">
							This creates the programme and one campaign per market, carries each market&rsquo;s agent, origin number
							and contact group onto it, and reads the client&rsquo;s do-not-call file into the suppression register
							against your name.
						</Typography>
						{groupsBound === markets.length && numbersReserved === markets.length ? null : (
							<Typography variant="body2" color="warning.main">
								{`${markets.length - groupsBound} market${
									markets.length - groupsBound === 1 ? "" : "s"
								} without a contact group and ${
									markets.length - numbersReserved
								} without an origin number will still be built — with nobody to dial, and with no number to dial from.`}
							</Typography>
						)}
						<Typography variant="body2" color="text.secondary">
							Safe to retry — existing campaigns are reused and unfinished configuration is reapplied.
						</Typography>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button color="secondary" onClick={() => setConfirmOpen(false)}>
						Cancel
					</Button>
					<Button variant="contained" disabled={provisionMutation.isPending} onClick={handleProvision}>
						Provision
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
}
